/**
 * @file care-reminder-instance.schema.ts
 * @description Instância de um lembrete para uma usuária — o estado dela em
 * relação a um `careItem` do tipo REMINDER.
 *
 * Separado da DEFINIÇÃO (`careItems`) pelo mesmo motivo que resposta é separada
 * de checkpoint: a definição é da equipe clínica, a instância é da usuária.
 *
 * O padrão de agendamento copia `appointments`, que já provou funcionar em
 * produção: lembretes pré-calculados no documento + `nextReminderDueAt`
 * espelhado para o dispatcher conseguir fazer query de range (o Firestore não
 * consulta dentro de array de objetos). Cloud Tasks foi cogitado e abandonado
 * no repo — `cloud-tasks.service.ts` está vazio de propósito.
 */

import { z } from 'zod';

/**
 * Máquina de estados canônica dos lembretes, extraída das especificações
 * clínicas (é a mesma em exames, vacinas e consultas):
 *
 *   PENDING      — ainda não respondeu. Card aparece.
 *   SCHEDULED    — tem data marcada. Dispara as notificações por offset.
 *   DONE         — realizou. Encerra o lembrete, oferece o repositório.
 *   DECLINED     — não pretende realizar. Sai da jornada, mas é REVERSÍVEL.
 *   NOT_INDICATED— o profissional não indicou. Encerra sem sugerir de novo.
 */
export const CareReminderStatusEnum = z.enum([
  'PENDING',
  'SCHEDULED',
  'DONE',
  'DECLINED',
  'NOT_INDICATED',
]);
export type CareReminderStatus = z.infer<typeof CareReminderStatusEnum>;

export const CareReminderDeliveryStatusEnum = z.enum(['PENDING', 'SENT', 'SKIPPED', 'FAILED']);

/** Uma notificação agendada. `dueAt` é absoluto, calculado a partir do registro. */
export const CareScheduledNotificationSchema = z.object({
  /** Corresponde ao id da notificação na definição do lembrete. */
  notificationId: z.string(),
  offsetMinutes: z.number().int(),
  dueAt: z.coerce.date(),
  status: CareReminderDeliveryStatusEnum.default('PENDING'),
  attempts: z.number().int().min(0).default(0),
  sentAt: z.coerce.date().nullable().default(null),
  /**
   * Canais que confirmaram entrega. Importa porque push ainda é stub: sem isto,
   * "enviado" incluiria notificações que nunca saíram, e o dado mentiria até
   * alguém investigar por que ninguém foi avisado.
   */
  deliveredChannels: z.array(z.string()).default([]),
  error: z.string().nullable().default(null),
});
export type CareScheduledNotification = z.infer<typeof CareScheduledNotificationSchema>;

/** Um registro do lembrete (uma consulta, um exame, uma dose). */
export const CareReminderRecordSchema = z.object({
  id: z.string(),
  /** Data/hora do evento. Ancora os offsets das notificações. */
  scheduledAt: z.coerce.date().nullable().default(null),
  /** Quando foi de fato realizado. */
  completedAt: z.coerce.date().nullable().default(null),
  /** Campos livres configurados na definição (profissional, local, observações...). */
  fields: z.record(z.any()).default({}),
  notifications: z.array(CareScheduledNotificationSchema).default([]),
  createdAt: z.coerce.date(),
});
export type CareReminderRecord = z.infer<typeof CareReminderRecordSchema>;

export const CareReminderInstanceSchema = z.object({
  id: z.string(),
  userId: z.string(),
  itemId: z.string(),
  itemVersion: z.number().int().positive(),
  pregnancyId: z.string().nullable().optional(),
  /**
   * Marca da sessão em que o registro aconteceu (A052, fase 2). Opcional
   * enquanto o dado antigo é backfillado; a leitura passa a filtrar por ele
   * na fase 3. Cada CRM vê só o que aconteceu na própria marca.
   */
  brandId: z.string().nullable().optional(),

  status: CareReminderStatusEnum.default('PENDING'),

  /** Registros. Item de registro único usa um só; cadastro usa vários. */
  records: z.array(CareReminderRecordSchema).default([]),

  /**
   * Espelho do menor `dueAt` PENDING entre todos os registros. O dispatcher
   * consulta por este campo — Firestore não olha dentro de arrays.
   */
  nextReminderDueAt: z.coerce.date().nullable().default(null),

  /**
   * Regra de acompanhamento: quando não há registro futuro, volta a lembrar
   * nesta data. É o "sem próxima consulta agendada → notificar em N dias" das
   * especificações.
   */
  followUpDueAt: z.coerce.date().nullable().default(null),

  /** Última resposta da usuária à pergunta de status, para o card refletir. */
  lastAnswerOptionId: z.string().nullable().default(null),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});

export type CareReminderInstance = z.infer<typeof CareReminderInstanceSchema>;

/**
 * Menor `dueAt` ainda pendente entre todos os registros, ou `null`.
 * Espelha `computeNextReminderDueAt` de `appointment.schema.ts` — mesma função,
 * outro agregado.
 */
export function computeCareNextReminderDueAt(
  records: Array<{ notifications?: Array<{ dueAt: Date; status?: string }> }>,
  followUpDueAt?: Date | null
): Date | null {
  const pendentes: Date[] = [];

  for (const record of records) {
    for (const notification of record.notifications ?? []) {
      if ((notification.status ?? 'PENDING') === 'PENDING') {
        pendentes.push(new Date(notification.dueAt));
      }
    }
  }
  if (followUpDueAt) pendentes.push(new Date(followUpDueAt));

  if (!pendentes.length) return null;
  return pendentes.reduce((menor, atual) => (atual < menor ? atual : menor));
}

/**
 * Estados terminais: o lembrete sai da jornada. Todos são reversíveis — a
 * especificação é explícita ("poderá atualizar sua resposta sempre que
 * necessário"), então nada aqui apaga dado.
 */
export const CARE_REMINDER_TERMINAL_STATUSES: CareReminderStatus[] = [
  'DONE',
  'DECLINED',
  'NOT_INDICATED',
];

export function isCareReminderTerminal(status: CareReminderStatus): boolean {
  return CARE_REMINDER_TERMINAL_STATUSES.includes(status);
}
